import React, { useState } from 'react';
import Filters from '../../../components/Filter';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { translate } from '../../../services/translate';
import { NavigationProp, RouteProp, useNavigation } from '@react-navigation/native';
import { CRMParamList } from '../../../typings/Params';
import FilterCheckBox from '../../../components/Filter/components/FilterCheckBox';
import CameraTypeDropdown from '../../../components/Filters/Cameras/CameraTypeDropdown';
import CameraManufacturerDropdown from '../../../components/crm/CameraManufacturerDropdown';
import InstallationCompanyDropdown from '../ManagementDetails/installationCompanyDropdown';
import InternetCompanyDropdown from '../ManagementDetails/InternetCompanyDropdown';
import { CameraType, AlgorithmFilter } from '../../../schemas/cameras';
import TagsDropdown from '../../../components/Filters/Cameras/CameraTagsDropdown';
import { Manufacturer } from '../../../schemas/crm';
import AlgorithmDropdown from '../../../components/AlgorithmDropdown';
import CameraStorageDropdown from '../../../components/crm/CameraStorageDropdown';
import { useCurrentApp } from '../../../context/CurrentApp';
import CompanyDropdown from '../../../components/CompanyDropdown';

interface Props {
    filters: CRMParamList['Management'];
    setFilters: React.Dispatch<React.SetStateAction<CRMParamList['Management']>>;
    textFilter: string;
    setTextFilter: React.Dispatch<React.SetStateAction<string>>;
}

function Filter({ filters, setFilters, textFilter, setTextFilter }: Props) {
    const navigation = useNavigation<NavigationProp<RouteProp<CRMParamList, 'Management'>>>();
    const { isParentApp } = useCurrentApp();

    const [offlineFilter, setOfflineFilter] = useState(filters.offline ?? false);
    const [selectedCameraTypeFilter, setSelectedCameraTypeFilter] = useState<CameraType | 'all'>(filters.type ?? 'all');
    const [selectedManufacturerFilter, setSelectedManufacturerFilter] = useState<Manufacturer | 'all'>(filters.manufacturer ?? 'all');
    const [selectedInstallationCompanyFilter, setSelectedInstallationCompanyFilter] = useState<number>(filters.installationCompanyId ?? 0);
    const [selectedInternetCompanyFilter, setSelectedInternetCompanyFilter] = useState<number>(filters.internetCompanyId ?? 0);
    const [selectedCompanyIdFilter, setSelectedCompanyIdFilter] = useState<number>(filters.companyId ?? 0);
    const [selectedTagsFilter, setSelectedTagsFilter] = useState<number[]>(filters.tagsIds ?? []);
    const [selectedAlgorithmFilter, setSelectedAlgorithmFilter] = useState<AlgorithmFilter | 'all'>('all');
    const [selectedStorageFilter, setSelectedStorageFilter] = useState<CameraStorage | 'all'>(filters.storage ?? 'all');

    const handleCleanFilter = () => {
        setTextFilter('');
        setOfflineFilter(false);
        setSelectedCameraTypeFilter('all');
        setSelectedManufacturerFilter('all');
        setSelectedInstallationCompanyFilter(0);
        setSelectedInternetCompanyFilter(0);
        setSelectedCompanyIdFilter(0);
        setSelectedTagsFilter([]);
        setSelectedAlgorithmFilter('all');
        setSelectedStorageFilter('all');
    };

    const handleFilter = () => {
        setFilters({
            ...filters,
            page: 0,
            textFilter: textFilter !== '' ? textFilter : undefined,
            offline: offlineFilter ? offlineFilter : undefined,
            tagsIds: selectedTagsFilter.length ? selectedTagsFilter : undefined,
            type: selectedCameraTypeFilter !== 'all' ? selectedCameraTypeFilter : undefined,
            manufacturer: selectedManufacturerFilter !== 'all' ? selectedManufacturerFilter : undefined,
            installationCompanyId: selectedInstallationCompanyFilter !== 0 ? selectedInstallationCompanyFilter : undefined,
            internetCompanyId: selectedInternetCompanyFilter !== 0 ? selectedInternetCompanyFilter : undefined,
            companyId: isParentApp && selectedCompanyIdFilter !== 0 ? selectedCompanyIdFilter : undefined,
            storage: isParentApp && selectedStorageFilter !== 'all' ? selectedStorageFilter : undefined,
            hasFacial: selectedAlgorithmFilter === 'hasFacial' || undefined,
            hasLpr: selectedAlgorithmFilter === 'hasLpr' || undefined,
            hasAreaInvasion: selectedAlgorithmFilter === 'hasAreaInvasion' || undefined,
        });

        navigation.setParams({
            page: 1,
            textFilter: textFilter !== '' ? textFilter : undefined,
            offline: offlineFilter ? offlineFilter : undefined,
            tagsIds: selectedTagsFilter.length ? selectedTagsFilter : undefined,
            type: selectedCameraTypeFilter !== 'all' ? selectedCameraTypeFilter : undefined,
            manufacturer: selectedManufacturerFilter !== 'all' ? selectedManufacturerFilter : undefined,
            installationCompanyId: selectedInstallationCompanyFilter !== 0 ? selectedInstallationCompanyFilter : undefined,
            internetCompanyId: selectedInternetCompanyFilter !== 0 ? selectedInternetCompanyFilter : undefined,
            companyId: isParentApp && selectedCompanyIdFilter !== 0 ? selectedCompanyIdFilter : undefined,
            storage: isParentApp && selectedStorageFilter !== 'all' ? selectedStorageFilter : undefined,
            hasFacial: selectedAlgorithmFilter === 'hasFacial' || undefined,
            hasLpr: selectedAlgorithmFilter === 'hasLpr' || undefined,
            hasAreaInvasion: selectedAlgorithmFilter === 'hasAreaInvasion' || undefined,
        });
    };

    return (
        <Filters onCleanFilters={handleCleanFilter} onFilters={handleFilter}>
            <FilterInput
                label={translate('typeToSearch')}
                value={textFilter}
                placeholder={translate('typeToSearch')}
                onChange={setTextFilter}
            />
            {isParentApp && <CompanyDropdown
                setValue={setSelectedCompanyIdFilter}
                value={selectedCompanyIdFilter}
                zIndex={8}
            />}
            <FilterCheckBox
                label={translate('offline')}
                value={offlineFilter}
                onChange={setOfflineFilter}
            />
            <CameraTypeDropdown
                value={selectedCameraTypeFilter}
                setValue={setSelectedCameraTypeFilter}
                includeAll={true}
                zIndex={7}
            />
            {isParentApp && <CameraStorageDropdown
                value={selectedStorageFilter as CameraStorage}
                setValue={setSelectedStorageFilter as React.Dispatch<React.SetStateAction<CameraStorage | undefined>>}
                zIndex={6}
                includeAllOptions={true}
                required={false}
            />}
            <CameraManufacturerDropdown
                value={selectedManufacturerFilter as Manufacturer}
                setValue={setSelectedManufacturerFilter as React.Dispatch<React.SetStateAction<Manufacturer | undefined>>}
                zIndex={5}
                includeAllOptions={true}
                required={false}
            />
            <InstallationCompanyDropdown
                value={selectedInstallationCompanyFilter}
                setValue={setSelectedInstallationCompanyFilter}
                zIndex={4}
                required={false}
                disabled={false}
            />
            <InternetCompanyDropdown
                value={selectedInternetCompanyFilter}
                setValue={setSelectedInternetCompanyFilter}
                zIndex={3}
                required={false}
                disabled={false}
            />
            <TagsDropdown
                value={selectedTagsFilter}
                setValue={setSelectedTagsFilter}
                zIndex={2}
            />
            <AlgorithmDropdown
                value={selectedAlgorithmFilter}
                setValue={setSelectedAlgorithmFilter}
                zIndex={1}
                required={false}
                disabled={false}
            />
        </Filters>
    );
}


export default Filter;
